/*
 * Downloading an attachment (2026-10-28, S47).
 *
 * Two steps, because the file never comes through this server and the storage
 * account takes no request it did not sign: ask the knowledge base for a link -
 * the server decides there and then whether this reader may have this file, and
 * signs one, read-only, for a few minutes - and send the browser to it. The
 * storage account answers with Content-Disposition: attachment, so the browser
 * saves the file and this page stays where it is.
 *
 * The link is followed by navigating the window rather than with an <a download>:
 * `download` is ignored on a cross-origin href, which the storage account always
 * is, and a fetch of the bytes would hold up to 25 MB in this page's memory
 * before the browser could save any of it.
 *
 * `navigate` is a parameter, as in followSignOutReturn, so the rule can be tested
 * without a browser that navigates.
 */

import type { AttachmentDownloadLink } from '@accotool-kb/shared';

/** The browser's own way to a download: the page stays, the file is saved. */
export function navigateToDownload(url: string): void {
  window.location.assign(url);
}

/**
 * Fetches the link and follows it. The link's errors are the caller's to show -
 * a 404 means this reader may not have the file, a 503 that the deployment has
 * no attachment store - so they are thrown, never swallowed.
 */
export async function downloadAttachment(
  fetchLink: () => Promise<AttachmentDownloadLink>,
  navigate: (url: string) => void = navigateToDownload,
): Promise<AttachmentDownloadLink> {
  const link = await fetchLink();
  navigate(link.url);
  return link;
}
